import React, { useState, useRef, useLayoutEffect } from 'react';

import { CommonProps, mosaiqStyled } from '../../lib';
import { FieldContainer } from '../../base';
import { Label } from '../Label';
import { Pill } from '../Pill';
import { Typography } from '../Typography';

import { InputPillsProps } from './types';
import { generateId } from '../../utils';
import { PillsWrapper, Field, FieldMinWidth } from './InputPills.styles';

const InputPills = <T,>({
  innerRef,
  label,
  helperText,
  autoSize = true,
  autoFocus = false,
  disabled = false,
  error = false,
  required = false,
  startAdornment,
  endAdornment,
  inputProps = {},
  items = [],
  renderPill,
  renderOverflowPill,
  ...props
}: InputPillsProps<T> & CommonProps<HTMLDivElement>) => {
  const { current: inputId } = useRef(generateId(label));
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pillsWrapperRef = useRef<HTMLDivElement | null>(null);

  const [focus, setFocus] = useState<boolean>(autoFocus);
  const [overflowingItems, setOverflowingItems] = useState<T[]>([]);

  useLayoutEffect(() => {
    if (autoSize || !pillsWrapperRef.current || !inputRef.current) return;

    const children = Array.from(pillsWrapperRef.current.children);

    const _overflowingItems: T[] = [];

    const wrapperWidth = pillsWrapperRef.current.offsetWidth;

    for (let i = children.length - 1; i > 0; i--) {
      const child = children[i] as HTMLElement;

      if (child.offsetLeft + child.offsetWidth > wrapperWidth - FieldMinWidth - 40) {
        _overflowingItems.push(items[i]);
      } else {
        break;
      }
    }

    if (_overflowingItems.length) {
      setOverflowingItems(_overflowingItems);
    } else {
      setOverflowingItems([]);
      inputRef.current.style.width = '';
    }
  }, [items, autoSize]);

  return (
    <FieldContainer ref={innerRef} {...props}>
      {!!label && (
        <label htmlFor={inputId}>
          <Label color={disabled ? 'placeholder' : 'body'}>
            {label}
            {required && <>*</>}
          </Label>
        </label>
      )}
      <Field
        readOnly={inputProps?.readOnly}
        disabled={disabled}
        error={error}
        focus={focus}
        autoSize={autoSize}
        onClick={(e) => (e.target as HTMLDivElement).querySelector('input')?.focus()}
      >
        {startAdornment}
        {!autoSize && (
          <PillsWrapper ref={pillsWrapperRef} aria-hidden>
            {items.map((item, i) => (
              <Pill key={i} {...renderPill(item, i)} />
            ))}
          </PillsWrapper>
        )}
        {items
          .filter((item) => !overflowingItems.includes(item))
          .map((item, i) => (
            <Pill key={i} {...renderPill(item, i)} />
          ))}
        {!!overflowingItems.length && !autoSize && renderOverflowPill && (
          <Pill {...renderOverflowPill(overflowingItems)} />
        )}
        <input
          {...inputProps}
          id={!!label ? inputId : undefined}
          disabled={disabled}
          autoFocus={autoFocus}
          onFocus={(e) => {
            setFocus(true);
            inputProps?.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocus(false);
            inputProps?.onBlur?.(e);
          }}
          ref={inputRef}
        />
        {endAdornment}
      </Field>
      {!!helperText && (
        <Typography variant="body4" color={error ? 'error' : disabled ? 'placeholder' : 'passive'}>
          {helperText}
        </Typography>
      )}
    </FieldContainer>
  );
};

InputPills.displayName = 'InputPills';

export default mosaiqStyled(InputPills);
